import { useCallback, useEffect, useState } from "react";
import { GUIDES, guideById, type Guide, type GuideId, type MeResponse } from "@handvero/shared";
import { Sheet } from "./ui";
import "./guide.css";

/*
 * Bebilderte Rundgänge zum Durchklicken (Inhalte in packages/shared/src/guides.ts). Sie erscheinen einmal von selbst,
 * lassen sich überspringen und über Vero oder „Mein Konto“ jederzeit wieder öffnen.
 */

const EVENT = "hv-guide";
const seenKey = (userId: string, id: GuideId) => `hv-guide-${userId}-${id}`;

function wasSeen(userId: string, id: GuideId): boolean {
  try {
    return localStorage.getItem(seenKey(userId, id)) === "1";
  } catch {
    return true; // ohne Speicher lieber nicht bei jedem Start stören
  }
}
function markSeen(userId: string, id: GuideId) {
  try {
    localStorage.setItem(seenKey(userId, id), "1");
  } catch {
    /* ohne Speicher erscheint der Rundgang beim nächsten Mal wieder */
  }
}

/** Öffnet einen Rundgang von überall aus, zum Beispiel aus Vero. */
export function openGuide(id: GuideId) {
  window.dispatchEvent(new CustomEvent(EVENT, { detail: id }));
}

export function GuideHost({ me, variant }: { me: MeResponse; variant: "desk" | "app" }) {
  const [open, setOpen] = useState<GuideId | null>(null);
  const userId = me.user.id;

  // Von selbst: erst der allgemeine Rundgang, danach, wenn das Paket freigeschaltet ist, der zum Paket (nur am PC, dort liegt es).
  useEffect(() => {
    if (open || me.support) return;
    if (!wasSeen(userId, "start")) setOpen("start");
    else if (variant === "desk" && me.addons.billing && !wasSeen(userId, "rechnungen")) setOpen("rechnungen");
  }, [open, me.support, me.addons.billing, userId, variant]);

  useEffect(() => {
    const on = (e: Event) => {
      const id = (e as CustomEvent<string>).detail;
      if (guideById(id)) setOpen(id as GuideId);
    };
    window.addEventListener(EVENT, on);
    return () => window.removeEventListener(EVENT, on);
  }, []);

  const close = useCallback(() => {
    if (open) markSeen(userId, open);
    setOpen(null);
  }, [open, userId]);

  const guide = open ? guideById(open) : undefined;
  return guide ? <GuideSheet guide={guide} variant={variant} onClose={close} /> : null;
}

function GuideSheet({ guide, variant, onClose }: { guide: Guide; variant: "desk" | "app"; onClose: () => void }) {
  const [i, setI] = useState(-1); // -1 = Begrüßung
  const step = i >= 0 ? guide.steps[i] : undefined;
  const last = i === guide.steps.length - 1;
  const image = step ? (variant === "app" && step.phoneImage ? step.phoneImage : step.image) : null;
  const phone = !!step && variant === "app" && !!step.phoneImage;
  return (
    <Sheet title={guide.title} onClose={onClose}>
      <div className="guide">
        {!step ? (
          <>
            <h3>Kurz erklärt, mit Bildern</h3>
            <p>{guide.intro}</p>
            <p className="muted-s">Das dauert eine Minute. Sie können jederzeit überspringen und den Rundgang später unter „Mein Konto“ oder über Vero wieder öffnen.</p>
          </>
        ) : (
          <>
            <figure className={`guide-shot${phone ? " phone" : ""}`}>
              <img src={`/screens/${image}.jpg`} alt={step.imageAlt} loading="eager" decoding="async" />
            </figure>
            <h3>{step.title}</h3>
            <p>{step.text}</p>
            <div className="guide-dots" aria-label={`Schritt ${i + 1} von ${guide.steps.length}`}>
              {guide.steps.map((_, n) => (
                <i key={n} className={n === i ? "on" : undefined} />
              ))}
            </div>
          </>
        )}
        <div className="guide-actions">
          <button className="app-btn small ghost" onClick={onClose}>
            {last ? "Schließen" : "Überspringen"}
          </button>
          <span className="guide-nav">
            {i >= 0 && (
              <button className="app-btn small ghost" onClick={() => setI(i - 1)}>
                Zurück
              </button>
            )}
            {!last && (
              <button className="app-btn small" onClick={() => setI(i + 1)}>
                {i < 0 ? "Los geht's" : "Weiter"}
              </button>
            )}
          </span>
        </div>
      </div>
    </Sheet>
  );
}

/** Liste zum Wiederöffnen, für „Mein Konto“. */
export function GuideList({ billing }: { billing: boolean }) {
  return (
    <div className="guide-list">
      {GUIDES.filter((g) => !g.addon || (g.addon === "billing" && billing)).map((g) => (
        <button key={g.id} className="app-btn small ghost" onClick={() => openGuide(g.id)}>
          {g.title}
        </button>
      ))}
    </div>
  );
}
